/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Custom render config for `react-native-toast-message`. The library's default
// renderers are hardcoded (white card + colored left bar) and ignore our
// class-based theming, so they look wrong in dark mode and don't follow light
// mode at all. These renderers match the in-app notification banner
// (`NotificationBannerHost`) — same panel surface, subtle border, drop shadow,
// design-token text — but MORE MINIMAL: a single status icon + title (+ optional
// one/two-line description), no logomark. Theme-aware via `dark:` classes, so it
// supports light mode automatically.
//
// Pass to the provider at the host-mount site: `<PingToastProvider config={pingToastConfig} />`.

import { AlertCircle, Check, Info } from 'lucide-react-native';
import { Text, View, type ViewStyle } from 'react-native';

/** Subtle elevation — kept inline so the cast follows the rounded corners
 *  (react-native-web converts shadow* → boxShadow). */
const TOAST_SHADOW = {
  shadowColor: '#000000',
  shadowOpacity: 0.18,
  shadowRadius: 12,
  shadowOffset: { width: 0, height: 6 },
  elevation: 6,
} as ViewStyle;

interface ToastRenderProps {
  readonly text1?: string | undefined;
  readonly text2?: string | undefined;
}

function ToastCard({
  text1,
  text2,
  icon,
}: ToastRenderProps & { readonly icon: JSX.Element }): JSX.Element {
  return (
    <View
      style={TOAST_SHADOW}
      className="mx-4 mb-1 min-w-[200px] max-w-[400px] flex-row items-center gap-2.5 rounded-xl border border-light-border-subtle dark:border-dark-border-subtle bg-light-surface-panel dark:bg-dark-surface-panel px-3.5 py-2.5"
    >
      <View className="shrink-0">{icon}</View>
      <View className="min-w-0 flex-1">
        {text1 !== undefined && text1.length > 0 ? (
          <Text
            numberOfLines={1}
            className="text-[13.5px] font-semibold leading-[18px] text-light-text-primary dark:text-dark-text-primary"
          >
            {text1}
          </Text>
        ) : null}
        {text2 !== undefined && text2.length > 0 ? (
          <Text
            numberOfLines={2}
            className="mt-0.5 text-[12px] leading-[16px] text-light-text-secondary dark:text-dark-text-secondary"
          >
            {text2}
          </Text>
        ) : null}
      </View>
    </View>
  );
}

/** Per-type renderers for `react-native-toast-message`'s `config` prop. */
export const pingToastConfig = {
  success: (props: ToastRenderProps): JSX.Element => (
    <ToastCard {...props} icon={<Check size={18} strokeWidth={2.5} color="#3CCB4F" />} />
  ),
  error: (props: ToastRenderProps): JSX.Element => (
    <ToastCard {...props} icon={<AlertCircle size={18} strokeWidth={2} color="#FF5353" />} />
  ),
  info: (props: ToastRenderProps): JSX.Element => (
    <ToastCard {...props} icon={<Info size={18} strokeWidth={2} color="#537EFF" />} />
  ),
};
